import { useState } from 'react';
import dynamic from 'next/dynamic';
import useBreakpoint from 'hooks/useBreakpoint';

// Interfaces
import type { CrashCourseArticle } from 'interfaces/content/articles/Articles';
import type { FilterSection, FilterValue } from 'components/CrashCourseFilter/CrashCoursesFilterSection';

// Constants
import { Microsites } from 'constants/constants';
import { UIFragments } from 'utils/log/constants/uiFragments';

// Utils
import type { EventData } from 'services/Gtm/entities';

// Components
import RightRailFlex from 'components/Ad/AdSlot/RightRailFlex';
import ErrorBoundary from 'components/ErrorBoundary';

// Styles
import type { BreadcrumbsProps } from 'interfaces/navigation/Breadcrumbs';
import Breadcrumbs from 'components/Breadcrumbs';
import * as S from './LandingPageStyles';

const CrashCourseFilters = dynamic(() => import('components/CrashCourseFilter'));
const CrashCourseHeader = dynamic(() => import('components/CrashCourseHeader'));
const Search = dynamic(() => import('components/Globals/Search'));
const Masonry = dynamic(() => import('components/CrashCourseMasonry'));

interface Props {
  searchValue: string;
  filters: FilterSection[];
  onSearch: (value: string) => void;
  onInput: (value: string) => void;
  onComplexityChange: (value: FilterValue, checked: boolean) => void;
  articles: CrashCourseArticle[];
  breadcrumbs: BreadcrumbsProps;
}

const CrashCourseLandingPage = ({
  searchValue,
  filters,
  onSearch,
  onInput,
  onComplexityChange,
  articles,
  breadcrumbs,
}: Props) => {
  const [selectedComplexityLevels, setSelectedComplexityLevels] = useState<string[]>([]);
  const isTablet = useBreakpoint('lgMax');
  const isMobile = useBreakpoint('mmMax');
  const isHybrid = useBreakpoint('hybridMax');
  const numCourses = articles.length;

  let columnCount;
  if (isMobile) {
    columnCount = 1;
  } else if (isHybrid) {
    columnCount = 2;
  } else {
    columnCount = 3;
  }

  const breadcrumbsData = breadcrumbs?.items.length
    ? [{ label: 'home', link: '/' }, ...breadcrumbs.items]
    : [{ label: 'home', link: '/' }];

  const updatebreadcrumbsData = isMobile ? breadcrumbsData.splice(0, 2) : breadcrumbsData;

  const handleComplexityChange = (value: FilterValue, isChecked: boolean) => {
    if (!value.complexity) return;
    let levels: string[] = [];

    if (isChecked) {
      levels = [...selectedComplexityLevels, value.complexity];
    } else {
      levels = selectedComplexityLevels.filter((comp) => comp !== value.complexity);
    }
    setSelectedComplexityLevels([...levels]);
    onComplexityChange(value, isChecked);
  };

  const handleDeleteComplexity = (value: string) => {
    const levels = selectedComplexityLevels.filter((level) => level !== value);
    setSelectedComplexityLevels(levels);
    onComplexityChange({ complexity: value }, false);
  };

  const trackingData = {
    eventCategory: 'navigation',
    platformCD: Microsites.Crypto,
  } as EventData;

  return (
    <S.PageWrapper>
      <Breadcrumbs
        items={updatebreadcrumbsData}
        isHubPage
      />
      <S.HeadingWrapper>
        <CrashCourseHeader className='heading' />
        {isTablet && (
          <>
            <S.Wrapper>
              <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_PAGE}>
                <Search
                  value={searchValue}
                  className='search sm mobile-search'
                  dataCy='crashCourseSearch'
                  onSearch={onSearch}
                  onInput={onInput}
                  trackingData={trackingData}
                />
                <CrashCourseFilters
                  label='Filters'
                  onChange={(value, checked) => handleComplexityChange(value, checked)}
                  isDropdown
                  filters={filters}
                  trackingData={trackingData}
                />
              </ErrorBoundary>
            </S.Wrapper>
            <S.SelectedFiltersWrappers>
              {selectedComplexityLevels &&
                selectedComplexityLevels.map((level) => (
                  <div
                    data-cy='selectedComplexity'
                    className='selected-filter'
                    key={level}
                  >
                    <span
                      className='close'
                      aria-label={`Remove filter value ${level}`}
                      role='button'
                      tabIndex={0}
                      onClick={() => handleDeleteComplexity(level)}
                      onKeyDown={() => handleDeleteComplexity(level)}
                    />
                    {level}
                  </div>
                ))}
            </S.SelectedFiltersWrappers>
          </>
        )}
        <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_PAGE}>
          {!isTablet && (
            <Search
              value={searchValue}
              className='search sm'
              dataCy='crashCourseSearch'
              onSearch={onSearch}
              onInput={onInput}
              trackingData={trackingData}
            />
          )}
        </ErrorBoundary>
      </S.HeadingWrapper>
      <S.ContentWrapper>
        {!isTablet && (
          <S.SidebarWrapper>
            <S.Sidebar data-cy='crashCourseSidebar'>
              <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_PAGE}>
                <CrashCourseFilters
                  filters={filters}
                  onChange={(value, checked) => handleComplexityChange(value, checked)}
                  trackingData={trackingData}
                />
              </ErrorBoundary>
              <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_PAGE}>
                <S.AdContainer>
                  <RightRailFlex
                    id={0}
                    dataCy='crashCourseLandingAd'
                  />
                </S.AdContainer>
              </ErrorBoundary>
            </S.Sidebar>
          </S.SidebarWrapper>
        )}
        <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_PAGE}>
          <Masonry
            className='cards-container'
            cards={articles}
            columnCount={columnCount}
          />
        </ErrorBoundary>
      </S.ContentWrapper>
      <ErrorBoundary fragment={UIFragments.CRYPTO_CRASH_COURSE_PAGE}>
        <S.ScreenReader
          aria-live='polite'
          aria-atomic
        >
          {numCourses} courses found
        </S.ScreenReader>
      </ErrorBoundary>
    </S.PageWrapper>
  );
};

export default CrashCourseLandingPage;
